import { useState, useEffect, useCallback, useRef } from 'react';
import {
  apiFetch,
  classifyReportAccessStatus,
  readApiErrorMessage,
  type ReportAccessState,
} from '../utils/apiClient';
import {
  resolveAnalysisFailureCopy,
  resolveAnalysisStatusCopy,
  statusProgressSignature,
  type AnalysisStatusFields,
} from '../utils/analysisStatusCopy';

interface AnalysisStatus extends AnalysisStatusFields {
  upload_id: string;
  status: 'queued' | 'processing' | 'completed' | 'error' | 'uploaded_no_processing' | 'failed';
  progress: number;
  currentStep: number;
  estimatedTime: number;
  message: string;
  timestamp: string;
  video_storage_path?: string;
}

interface UseAnalysisStatusProps {
  analysisId: string;
  onComplete?: (status: AnalysisStatus) => void;
  onError?: (error: string) => void;
  enableNotifications?: boolean;
  backgroundMode?: boolean;
  onNavigate?: (path: string, state?: any) => void;
}

const POLL_INTERVAL_MS = 5000;

export const useAnalysisStatus = ({
  analysisId,
  onComplete,
  onError,
  enableNotifications = true,
  backgroundMode = false,
  onNavigate
}: UseAnalysisStatusProps) => {
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string>('');
  const [displayMessage, setDisplayMessage] = useState<string>('');
  const [accessState, setAccessState] = useState<ReportAccessState>(null);

  const isPollingRef = useRef(false);
  const pollCountRef = useRef(0);
  const retryCountRef = useRef(0);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingStartedRef = useRef(false);
  const mountedRef = useRef(true);
  const statusRef = useRef<AnalysisStatus | null>(null);
  const progressSignatureRef = useRef<string | null>(null);
  const progressUnchangedSinceRef = useRef<number | null>(null);
  const staleMsRef = useRef(0);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onNavigateRef = useRef(onNavigate);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    onNavigateRef.current = onNavigate;
  });

  const clearPollTimeout = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (!enableNotifications || !('Notification' in window)) return false;

    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }, [enableNotifications]);

  const showNotification = useCallback((title: string, body: string, icon?: string) => {
    if (!enableNotifications || Notification.permission !== 'granted') return;

    new Notification(title, {
      body,
      icon: icon || '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'scoutlytics-analysis',
      requireInteraction: true
    });
  }, [enableNotifications]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    pollingStartedRef.current = false;
    clearPollTimeout();
    if (mountedRef.current) {
      setIsPolling(false);
    }
  }, [clearPollTimeout]);

  const startPolling = useCallback(async () => {
    if (!analysisId || pollingStartedRef.current) return;

    pollingStartedRef.current = true;

    if (enableNotifications) {
      await requestNotificationPermission();
    }

    if (!mountedRef.current || !analysisId) {
      pollingStartedRef.current = false;
      return;
    }

    isPollingRef.current = true;
    setIsPolling(true);
    pollCountRef.current = 0;
    retryCountRef.current = 0;
    setError('');
    setAccessState(null);
    setDisplayMessage('');
    progressSignatureRef.current = null;
    progressUnchangedSinceRef.current = null;
    staleMsRef.current = 0;

    const updateStaleTracking = (data: AnalysisStatus) => {
      const signature = statusProgressSignature(data);
      const now = Date.now();
      if (progressSignatureRef.current !== signature) {
        progressSignatureRef.current = signature;
        progressUnchangedSinceRef.current = now;
        staleMsRef.current = 0;
        return;
      }
      if (progressUnchangedSinceRef.current != null) {
        staleMsRef.current = now - progressUnchangedSinceRef.current;
      }
    };

    const applyPresentation = (data: AnalysisStatus) => {
      const presentation = resolveAnalysisStatusCopy(data, {
        staleMs: staleMsRef.current,
      });
      const parts = [presentation.headline, presentation.detail].filter(Boolean);
      if (presentation.staleNotice) {
        parts.push(`${presentation.staleNotice.title}: ${presentation.staleNotice.body}`);
      }
      setDisplayMessage(parts.join(' — '));
    };

    const scheduleNextPoll = () => {
      if (!isPollingRef.current || !mountedRef.current) return;
      clearPollTimeout();
      pollTimeoutRef.current = setTimeout(() => {
        pollTimeoutRef.current = null;
        void pollStatusInternal();
      }, POLL_INTERVAL_MS);
    };

    const pollStatusInternal = async () => {
      if (!isPollingRef.current || !analysisId || !mountedRef.current) return;

      try {
        const response = await apiFetch(`/api/status/${analysisId}`);

        if (!response.ok) {
          const access = classifyReportAccessStatus(response.status);
          if (access) {
            const message = (await readApiErrorMessage(response)) || '';
            setAccessState(access);
            setError(
              access === 'forbidden'
                ? message || 'This report belongs to another account.'
                : access === 'not_found'
                  ? message || 'Report not found.'
                  : message || 'Please sign in to view this report.'
            );
            stopPolling();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: AnalysisStatus = await response.json();

        if (!isPollingRef.current || !mountedRef.current) return;

        const previous = statusRef.current;
        statusRef.current = data;
        setStatus(data);
        setError('');
        updateStaleTracking(data);
        applyPresentation(data);

        if (data.status === 'completed') {
          stopPolling();

          if (onCompleteRef.current) {
            onCompleteRef.current(data);
          }

          if (enableNotifications) {
            showNotification('Analysis Complete!', 'Your video analysis is ready to view.');
          }

          return;
        }

        if (data.status === 'failed' || data.status === 'error') {
          stopPolling();
          const failure = resolveAnalysisFailureCopy(data);
          const failureMessage = failure?.message || data.message || 'Analysis failed';
          setError(failureMessage);
          setDisplayMessage(failureMessage);

          if (onErrorRef.current) {
            onErrorRef.current(failureMessage);
          }

          if (enableNotifications) {
            showNotification(
              failure?.title || 'Analysis Failed',
              failureMessage
            );
          }

          return;
        }

        if (
          previous &&
          (data.currentStep > previous.currentStep || data.progress > previous.progress)
        ) {
          pollCountRef.current = 0;
        }

        pollCountRef.current++;
        const maxPolls = 120 * 60 / (POLL_INTERVAL_MS / 1000);
        if (pollCountRef.current < maxPolls) {
          scheduleNextPoll();
        } else {
          stopPolling();
          const timeoutMessage = 'Analysis timeout - please check back later';
          setError(timeoutMessage);
          setDisplayMessage(timeoutMessage);
        }
      } catch (pollError) {
        console.error('Polling error:', pollError);

        if (!isPollingRef.current || !mountedRef.current) return;

        retryCountRef.current++;

        if (retryCountRef.current < 3) {
          scheduleNextPoll();
        } else {
          stopPolling();
          const networkError = 'Failed to check analysis status';
          setError(networkError);
          setDisplayMessage(networkError);

          if (onErrorRef.current) {
            onErrorRef.current('Failed to check analysis status');
          }
        }
      }
    };

    void pollStatusInternal();
  }, [
    analysisId,
    enableNotifications,
    requestNotificationPermission,
    showNotification,
    stopPolling,
    clearPollTimeout,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [analysisId, stopPolling]);

  return {
    status,
    isPolling,
    error,
    accessState,
    displayMessage,
    startPolling,
    stopPolling,
    showNotification
  };
};
